import { SetStateAction, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cn from "classnames";

import { WizardButtons } from "shared/components/WizardNew/components/WizardButtons";
import { WizardData } from "shared/components/WizardNew/types";

import { SPLIT_TEST } from "shared/constants/split";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { WizardStore, WizardType } from "shared/context/WizardStoreContext";

import { simplyClick } from "shared/helpers/analytics";
import { isSplitTestRequired } from "shared/helpers/view";
import { postWizardData, setStepperState } from "shared/helpers/wizard";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

import styles from "@platformAssets/components/Wizard.module.scss";

type Props = { wizardData: WizardData };

const WizardWelcomePreferences = ({ wizardData }: Props) => {
  const { t } = useTranslation("wizardNew");

  const [wizardStore, setWizardStore] = useContext(WizardStore);

  const navigate = useNavigate();

  const wizard = useWizard(wizardData);
  const step = wizard[WizardStep.WelcomeStep1];
  const { mode } = wizardData;

  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  const handleClick = async () => {
    await postWizardData({}, WizardStep.WelcomeStep1);

    // Remove after refactoring helpers
    setWizardStore(
      setStepperState(
        wizardStore,
        1,
        mode,
        isSplitReducedWizardEnabled
      ) as unknown as SetStateAction<WizardType>
    );

    step.analytics?.start?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    navigate(`/${step.nextStep}`);
  };

  return (
    <div className={styles.welcomeContainer}>
      <WizardButtons variant="welcome" showBack={false} userData={null} />

      <div className={styles.welcome}>
        <div className={styles.wizardAnimationWrapper}>
          <div className={cn(styles.welcomeAnimation, styles.animation1)}>
            <div
              className={cn(styles.row1, {
                [styles.multiGender]: mode === SAFE_MODE_WIZARD,
              })}
            />
            <div
              className={cn(styles.row2, {
                [styles.multiGender]: mode === SAFE_MODE_WIZARD,
              })}
            />
            <div
              className={cn(styles.row3, {
                [styles.multiGender]: mode === SAFE_MODE_WIZARD,
              })}
            />
          </div>
        </div>

        <div className={styles.wizardInfoWrapper}>
          <div className={styles.welcomeTextWrapper}>
            <div>
              <div
                className={cn(styles.welcomeTitle, {
                  [styles.multiGender]: mode === SAFE_MODE_WIZARD,
                })}
              >
                <Trans
                  t={t}
                  i18nKey="wizardNew:block1.title"
                  components={{ 0: <br /> }}
                  values={{
                    userName: wizard?.data?.username,
                    brandName: import.meta.env.VITE_APP_MULTI_SITE_BRAND_NAME,
                  }}
                />
              </div>
              <div
                className={cn(styles.welcomeDescription, {
                  [styles.multiGender]: mode === SAFE_MODE_WIZARD,
                })}
              >
                {mode === SAFE_MODE_WIZARD
                  ? t("block1.descriptionMulti")
                  : t("block1.description")}
              </div>
            </div>
          </div>

          <div className={styles.btnWrap}>
            <button onClick={handleClick} className={styles.welcomeButton}>
              {t("block1.btn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { WizardWelcomePreferences };
